import React, {useMemo, useState} from "react";
import {Box, Button, Collapse, Group, Stack, Text, TextInput, Tooltip} from '@mantine/core';
import {DataTable} from "mantine-datatable";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {startAuthentication, type PublicKeyCredentialRequestOptionsJSON} from "@simplewebauthn/browser";
import {closeModal, openModal} from "@mantine/modals";
import {notifications} from "@mantine/notifications";
import {jwtDecode} from "jwt-decode";
import {
    Archive as ArchiveRecord,
    ArchiveService,
    PasskeyService,
    UserService,
} from "../../client";
import {PageLayout} from '../shell/PageLayout';
import {useAuthStore} from "../../store/authStore";
import {formatRequestError} from "../../utils/formatRequestError";
import {canMoveToArchive, normalizeRole} from '../../utils/roles';
import {useDataTablePagination} from '../../utils/useDataTablePagination';

const passkeySupported = typeof window !== 'undefined' && !!window.PublicKeyCredential;

const STATISTICS_QUERY_KEYS = [
    'statistics-registrations-per-day',
    'statistics-food',
    'statistics-open-registrations',
    'statistics-t-shirts',
] as const;

type MoveToArchiveConfirmationProps = {
    username: string;
    hasPasskey: boolean;
};

function MoveToArchiveConfirmation({username, hasPasskey}: MoveToArchiveConfirmationProps) {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const queryClient = useQueryClient();

    const finishMove = () => ArchiveService.moveRegistrationsToArchiveApiV1ArchivePost()
        .then((response) => {
            const moved = typeof response === 'object' && response !== null && 'moved' in response
                ? Number((response as {moved: number}).moved)
                : 0;
            queryClient.invalidateQueries({queryKey: ['archive']});
            queryClient.invalidateQueries({queryKey: ['registrations']});
            STATISTICS_QUERY_KEYS.forEach((queryKey) => {
                queryClient.invalidateQueries({queryKey: [queryKey]});
            });
            queryClient.invalidateQueries({queryKey: ['archive-move-status']});
            notifications.show({
                color: 'green',
                title: 'Registrations archived',
                message: moved === 1
                    ? '1 registration was moved to the archive.'
                    : `${moved} registrations were moved to the archive.`,
            });
            closeModal('moveToArchive');
        })
        .catch((error: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Could not move registrations to archive',
                message: formatRequestError(error, 'Request failed'),
            });
        });

    const moveWithPassword = () => {
        if (!username || !password) {
            return;
        }
        setLoading(true);
        UserService.loginForAccessTokenApiV1UserLoginPost({
            formData: {username, password},
        })
            .then(() => finishMove())
            .catch((error: unknown) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not move registrations to archive',
                    message: formatRequestError(error, 'Request failed'),
                });
            })
            .finally(() => setLoading(false));
    };

    const moveWithPasskey = async () => {
        if (!username) {
            return;
        }
        if (!passkeySupported) {
            notifications.show({
                color: 'yellow',
                title: 'Passkey unavailable',
                message: 'Passkeys require HTTPS (e.g. mkcert for local development).',
            });
            return;
        }
        setLoading(true);
        try {
            const optionsResponse = await PasskeyService.passkeyLoginOptionsApiV1PasskeyLoginOptionsPost({
                username: username.trim(),
            });
            if (!optionsResponse.has_passkey || !optionsResponse.options || !optionsResponse.challenge_token) {
                notifications.show({
                    color: 'red',
                    title: 'Could not move registrations to archive',
                    message: 'No passkey registered for this user.',
                });
                return;
            }
            const credential = await startAuthentication({
                optionsJSON: optionsResponse.options as unknown as PublicKeyCredentialRequestOptionsJSON,
            });
            await PasskeyService.passkeyLoginVerifyApiV1PasskeyLoginVerifyPost({
                credential: credential as unknown as Record<string, unknown>,
                challenge_token: optionsResponse.challenge_token,
                username: username.trim(),
            });
            await finishMove();
        } catch (error: unknown) {
            notifications.show({
                color: 'red',
                title: 'Could not move registrations to archive',
                message: formatRequestError(error, 'Authentication was cancelled or failed.'),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack gap="xs">
            <Text size="sm">
                This will move all current registrations into the archive and remove them from the active list.{' '}
                {hasPasskey
                    ? 'Confirm with your passkey to continue.'
                    : 'Enter your password to confirm.'}
            </Text>
            {hasPasskey ? (
                <>
                    {!passkeySupported ? (
                        <Text size="sm" c="dimmed">
                            Passkeys require HTTPS (e.g. mkcert for local development).
                        </Text>
                    ) : null}
                    <Group gap="xs">
                        <Button
                            color="red"
                            loading={loading}
                            disabled={!passkeySupported}
                            onClick={() => void moveWithPasskey()}
                        >
                            Confirm with passkey
                        </Button>
                        <Button variant="default" onClick={() => closeModal('moveToArchive')}>Cancel</Button>
                    </Group>
                </>
            ) : (
                <>
                    <TextInput
                        label="Password"
                        type="password"
                        required
                        placeholder="Your password"
                        value={password}
                        onChange={(e) => setPassword(e.currentTarget.value)}
                    />
                    <Group gap="xs">
                        <Button color="red" loading={loading} onClick={moveWithPassword}>
                            Move to archive
                        </Button>
                        <Button variant="default" onClick={() => closeModal('moveToArchive')}>Cancel</Button>
                    </Group>
                </>
            )}
        </Stack>
    );
}

type ArchiveYearSectionProps = {
    year: number;
    records: ArchiveRecord[];
    expanded: boolean;
    isLoading: boolean;
    onToggle: () => void;
};

function ArchiveYearSection({
    year,
    records,
    expanded,
    isLoading,
    onToggle,
}: ArchiveYearSectionProps) {
    const {paginate, getPaginationProps} = useDataTablePagination();
    const pageRecords = useMemo(
        () => paginate(records),
        [records, paginate],
    );

    const vegetarians = records.filter((item) => item.food === 'vegetarian').length;
    const vegan = records.filter((item) => item.food === 'vegan').length;
    const mixed = records.filter((item) => item.food === 'mixed').length;
    let foodString = "";
    if (vegan > 0) {
        foodString += " ┊ 🌱 ↦ " + vegan;
    }
    if (vegetarians > 0) {
        foodString += " ┊ 🥗 ↦ " + vegetarians;
    }
    if (mixed > 0) {
        foodString += " ┊ 🥩 ↦ " + mixed;
    }
    const shirtSizes: Map<string, number> = new Map();
    records.forEach((item) => {
        if (item.tshirt) {
            shirtSizes.set(item.tshirt, (shirtSizes.get(item.tshirt) ?? 0) + 1);
        }
    });
    let shirtString = "";
    Array.from(shirtSizes.keys()).forEach((item) => {
        shirtString += item.toUpperCase() + ": " + shirtSizes.get(item) + " ┊ ";
    });
    const ages: Map<number, number> = new Map();
    records.forEach((item) => {
        ages.set(item.age, (ages.get(item.age) ?? 0) + 1);
    });
    let agesString = "";
    Array.from(ages.keys()).forEach((item) => {
        agesString += item + "y: " + ages.get(item) + " ┊ ";
    });

    return (
        <Box m={2}>
            <Group justify="left" mb={5}>
                <Button onClick={onToggle} fullWidth variant="outline">{year}</Button>
                <Box>
                    <Text c="dimmed" mb={10} size="sm">{records.length} Registrations {foodString}</Text>
                    <Text c="dimmed" mb={10} size="sm">T-Shirts: {shirtString}</Text>
                    <Text c="dimmed" mb={10} size="sm">Ages: {agesString}</Text>
                </Box>
            </Group>
            <Collapse expanded={expanded} transitionTimingFunction="ease" mt={5}>
                <DataTable
                    withTableBorder
                    striped
                    highlightOnHover
                    records={pageRecords}
                    fetching={isLoading}
                    loaderType='oval'
                    {...getPaginationProps(records.length)}
                    columns={[
                        {
                            accessor: 'notice', title: 'Note', sortable: false,
                            render: ({notice}) => notice || '-',
                        },
                        {
                            accessor: 'food', title: 'Food', sortable: false,
                            render: ({food}) => food || '-',
                        },
                        {accessor: 'age', title: 'Age', sortable: false},
                        {accessor: 'tshirt', title: 'T-Shirt', sortable: false},
                    ]}
                />
            </Collapse>
        </Box>
    );
}

export const Archive = () => {
    const token = useAuthStore(state => state.token);
    const username = useMemo(() => {
        if (!token) {
            return '';
        }
        try {
            return jwtDecode<{sub?: string}>(token).sub ?? '';
        } catch {
            return '';
        }
    }, [token]);

    const {data: currentUserProfile} = useQuery({
        queryKey: ['current-user'],
        queryFn: UserService.getCurrentUserApiV1UserMeGet,
    });
    const isAdmin = canMoveToArchive(normalizeRole(currentUserProfile?.role));

    const {data: passkeyStatus} = useQuery({
        queryKey: ['passkey-status'],
        queryFn: PasskeyService.passkeyStatusApiV1PasskeyStatusGet,
        enabled: isAdmin,
    });

    const {data: moveStatus} = useQuery({
        queryKey: ['archive-move-status'],
        queryFn: ArchiveService.getArchiveMoveStatusApiV1ArchiveMoveStatusGet,
        enabled: isAdmin,
    });

    const {isLoading, data} = useQuery({
        queryKey: ['archive'],
        queryFn: ArchiveService.getArchiveApiV1ArchiveGet,
    });

    const [toggles, setToggles] = useState([false, false, false]);

    function handleToggle(index: number) {
        setToggles((prevToggles) =>
            prevToggles.map((toggle, i) => (i === index ? !toggle : toggle))
        );
    }

    const showMoveToArchiveModal = () => {
        if (moveStatus?.during_camp) {
            notifications.show({
                color: 'yellow',
                title: 'Archive move unavailable',
                message: `Registrations cannot be archived during camp (${moveStatus.date_start} to ${moveStatus.date_end}).`,
            });
            return;
        }
        openModal({
            modalId: 'moveToArchive',
            title: 'Move registrations to archive',
            children: (
                <MoveToArchiveConfirmation
                    username={username}
                    hasPasskey={passkeyStatus?.has_passkey ?? false}
                />
            ),
        });
    };

    const moveButton = (
        <Button
            color="red"
            variant="outline"
            disabled={moveStatus?.during_camp ?? false}
            onClick={showMoveToArchiveModal}
        >
            Move registrations to archive
        </Button>
    );

    const yearlyDataTable = (year: number, index: number) => {
        if (!data) {
            return <></>;
        }
        return (
            <ArchiveYearSection
                key={year}
                year={year}
                records={data[year]}
                expanded={toggles[index]}
                isLoading={isLoading}
                onToggle={() => handleToggle(index)}
            />
        );
    };

    if (!data) {
        return <></>;
    }

    const years: number[] = Object.keys(data) as unknown as number[];
    years.sort((a, b) => a < b ? 1 : -1);


    const allTables = years.map((year, index) => {
        return yearlyDataTable(year, index);
    })

    const campTooltipLabel = moveStatus?.during_camp
        ? `Unavailable during camp (${moveStatus.date_start} to ${moveStatus.date_end})`
        : undefined;

    return (
        <PageLayout
            title="Archive"
            description="Historical registration data by camp year."
            actions={isAdmin ? (
                campTooltipLabel ? (
                    <Tooltip label={campTooltipLabel}>
                        <span>{moveButton}</span>
                    </Tooltip>
                ) : moveButton
            ) : null}
        >
            {allTables}
            <div key="legend">
                <Box>
                    <Group justify="center" m={5}>
                        <Text c="dimmed" size="sm">Legend: 🌱 ↦ Vegan ┊ 🥗 ↦ Vegetarian ┊ 🥩 ↦ Mixed</Text>
                    </Group>
                </Box>
            </div>
        </PageLayout>
    );
}
